import { AnchorLayerType } from '@/utils/constant';
import { toBdPoint } from '../geometry';
import { isSelfIntersecting, pixelDistance } from '../geoUtils';
import { MapEventManager } from '../events';
import { DrawingRenderer } from '../renderers/DrawingRenderer';
import { DrawingValidator } from '../validators/DrawingValidator';
import type { LayerRegistry } from '../layerRegistry';

export interface DrawingHost {
  options: {
    drawCallBack?: (
      result: boolean,
      feature?: any,
      id?: string | number,
      coordinates?: any,
    ) => void;
    onDrawError?: (reason: string) => void;
    onDrawCancel?: () => void;
  };
  drawPolygon: (
    id: string | number,
    points: any,
    type: AnchorLayerType,
    val: any,
  ) => any;
}

/**
 * 绘制交互状态机
 *
 * 层级：map-core（"地图怎么动"）
 *
 * 职责：
 * 1. 维护绘制生命周期（开始 / 进行中 / 闭合 / 取消）
 * 2. 通过 MapEventManager 订阅地图事件，处理点击、鼠标移动
 * 3. 监听键盘事件（ESC 取消、Backspace 撤回）
 * 4. 调度 DrawingRenderer 做视觉反馈，调度 DrawingValidator 做校验
 *
 * 不关心：业务逻辑、React 状态、覆盖物样式管理
 */
export class DrawingInteraction {
  drawing = false;
  points: [number, number][] = [];

  /** 吸附像素阈值 */
  private static readonly SNAP_PX = 20;
  /** 双击识别时间阈值（ms） */
  private static readonly DBL_CLICK_MS = 300;
  /** 双击识别像素阈值 */
  private static readonly DBL_CLICK_PX = 10;
  /** 进入绘制后短暂忽略点击，避免触发绘制的那个点击被误收为顶点 */
  private static readonly START_COOLDOWN_MS = 200;

  private renderer: DrawingRenderer;
  private validator: DrawingValidator;
  private unsubscribers: Array<() => void> = [];
  private keyDownHandler: ((e: KeyboardEvent) => void) | null = null;
  private ignoreClickUntil = 0;
  private rafId: number | null = null;
  private pendingMouseMove: any = null;
  /** 上一次 click 的时间和像素位置，用于手动识别双击 */
  private lastClickInfo: { time: number; pixel: { x: number; y: number } } | null = null;

  constructor(
    private map: any,
    private engine: any,
    private events: MapEventManager,
    private layers: LayerRegistry,
    private host: DrawingHost
  ) {
    this.renderer = new DrawingRenderer(map);
    this.validator = new DrawingValidator();
  }

  /** 是否正在绘制 */
  isDrawing() {
    return this.drawing;
  }

  /**
   * 设置绘制期视觉样式覆盖，用于轻图测试等按子类型着色的场景
   */
  setDrawingStyle(override: Partial<{
    lineColor: string;
    polygonFill: string;
    polygonStroke: string;
    pointFill: string;
  }>) {
    this.renderer.setStyleOverride(override);
  }

  /** 开始一次新的绘制会话 */
  start() {
    if (this.drawing) {
      return;
    }
    this.drawing = true;
    this.points = [];
    this.lastClickInfo = null;
    this.ignoreClickUntil = Date.now() + DrawingInteraction.START_COOLDOWN_MS;

    this.bindEvents();
  }

  /** 取消当前绘制并清理视觉元素 */
  cancel() {
    if (!this.drawing) return;
    this.drawing = false;
    this.points = [];
    this.lastClickInfo = null;
    this.renderer.clear();
    this.unbindEvents();
    this.host.options?.onDrawCancel?.();
  }

  private bindEvents() {
    this.unsubscribers.push(
      this.events.on('map:click', (e) => this.handleClick(e)),
      this.events.on('map:mousemove', (e) => this.handleMouseMove(e))
    );

    this.keyDownHandler = (e: KeyboardEvent) => this.handleKeyDown(e);
    document.addEventListener('keydown', this.keyDownHandler);
  }

  private unbindEvents() {
    this.unsubscribers.forEach((unsub) => unsub());
    this.unsubscribers = [];

    if (this.rafId != null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.pendingMouseMove = null;

    if (this.keyDownHandler) {
      document.removeEventListener('keydown', this.keyDownHandler);
      this.keyDownHandler = null;
    }
  }

  private handleClick(e: any) {
    const now = Date.now();

    if (!this.drawing) return;

    // 冷却期：开始绘制或双击后短暂忽略点击，避免误触
    if (now < this.ignoreClickUntil) return;

    const lngLat = e?.lngLat;
    const pixel = e?.pixel ?? { x: 0, y: 0 };
    if (!lngLat || (lngLat[0] === 0 && lngLat[1] === 0)) return;

    const point: [number, number] = [
      Number(lngLat[0].toFixed(10)),
      Number(lngLat[1].toFixed(10)),
    ];

    // 双击完成绘制：
    // 全局禁用 BMapGL 双击缩放后，BMapGL 不再派发 dblclick 事件，
    // 因此通过时间和像素距离手动识别双击，避免依赖 domEvent.detail
    //
    // 注意：lastClickInfo 只记录“成功添加顶点的点击”。校验失败的点击会被清空，
    // 否则在自相交/重复点位置连续点击会被误识别为双击，导致意外完成绘制。
    if (this.lastClickInfo) {
      const dt = now - this.lastClickInfo.time;
      const dist = Math.hypot(
        pixel.x - this.lastClickInfo.pixel.x,
        pixel.y - this.lastClickInfo.pixel.y
      );
      if (dt < DrawingInteraction.DBL_CLICK_MS && dist < DrawingInteraction.DBL_CLICK_PX && this.points.length > 0) {
        // 双击前先校验当前多边形是否合法，避免误触导致意外完成
        const validation = this.validator.validatePolygon(this.points);
        if (validation.valid) {
          this.finish();
        } else {
          this.handleDrawFail(DrawingValidator.getReasonText(validation.reason!));
        }
        return;
      }
    }

    e?.domEvent?.stopPropagation?.();

    // 首点吸附检测：点数 >= 3 且光标在起点吸附范围内，直接闭合
    // 起点和光标统一用 pointToOverlayPixel 计算，保持坐标系一致
    if (this.points.length >= 3) {
      const startPixel = this.engine.pointToOverlayPixel(toBdPoint(this.points[0]));
      const cursorPixel = this.engine.pointToOverlayPixel(toBdPoint(point));
      if (
        startPixel &&
        cursorPixel &&
        pixelDistance(startPixel, cursorPixel) <= DrawingInteraction.SNAP_PX
      ) {
        this.finish();
        return;
      }
    }

    const validation = this.validator.validateNewPoint(point, this.points);
    if (!validation.valid) {
      const text = DrawingValidator.getReasonText(validation.reason!);
      this.host.options?.onDrawError?.(text);
      // 失败的点击不能作为双击候选，否则连续点击同一位置会被误识别为双击完成
      this.lastClickInfo = null;
      return;
    }

    // 新点合法：添加顶点并记录为双击候选
    this.points.push(point);
    this.renderer.addVertexMarker(point);
    this.renderer.updateTempline(this.points);
    this.lastClickInfo = { time: now, pixel };
  }

  private handleMouseMove(e: any) {
    if (!this.drawing || this.points.length < 1) return;

    this.pendingMouseMove = e;
    if (this.rafId == null) {
      this.rafId = requestAnimationFrame(() => this.flushMouseMove());
    }
  }

  private flushMouseMove() {
    this.rafId = null;
    const e = this.pendingMouseMove;
    this.pendingMouseMove = null;
    if (!e || this.points.length < 1) return;

    const lngLat = e?.lngLat;
    if (!lngLat) return;

    const cursor: [number, number] = [
      Number(lngLat[0].toFixed(10)),
      Number(lngLat[1].toFixed(10)),
    ];

    const effectiveCursor = this.applySnap(cursor);
    const lastPoint = this.points[this.points.length - 1];

    const validation = this.validator.validateNewPoint(effectiveCursor, this.points);
    const wouldIntersect = !validation.valid && validation.reason === 'would_self_intersect';

    // 预览面仅在不会自相交且构成简单多边形时渲染，与旧 drawing.ts 行为一致
    const previewPoints = [...this.points, effectiveCursor];
    const previewSelfIntersect =
      this.points.length >= 2 && isSelfIntersecting(previewPoints);
    const shouldRenderPreview = !wouldIntersect && !previewSelfIntersect;

    this.renderer.updateHintline(lastPoint, effectiveCursor, wouldIntersect);
    this.renderer.updatePreviewPolygon(
      this.points,
      effectiveCursor,
      shouldRenderPreview
    );

    // 实时更新每条已绘制边（含动态边）的长度标注
    this.renderer.updateEdgeLabels(this.points, effectiveCursor);
  }

  /**
   * 磁吸：当光标进入起点吸附范围时，将坐标替换为首点坐标
   */
  private applySnap(cursor: [number, number]): [number, number] {
    if (this.points.length < 3) return cursor;

    const startPixel = this.engine.pointToOverlayPixel(toBdPoint(this.points[0]));
    const cursorPixel = this.engine.pointToOverlayPixel(toBdPoint(cursor));
    if (!startPixel || !cursorPixel) return cursor;

    if (pixelDistance(startPixel, cursorPixel) <= DrawingInteraction.SNAP_PX) {
      this.renderer.showSnapRing(this.points[0]);
      return this.points[0];
    }

    this.renderer.hideSnapRing();
    return cursor;
  }

  private handleKeyDown(e: KeyboardEvent) {
    if (!this.drawing) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      this.cancel();
    } else if (e.key === 'Backspace') {
      e.preventDefault();
      this.undoLastPoint();
    }
  }

  private undoLastPoint() {
    if (this.points.length === 0) return;
    this.points.pop();
    this.lastClickInfo = null;
    this.renderer.removeLastVertexMarker();
    this.renderer.updateTempline(this.points);
    this.renderer.clearHintline();
    this.renderer.clearPreviewPolygon();
  }

  /**
   * 绘制失败处理：
   * 清空已绘制的临时图形和顶点，但保持绘制会话和事件监听，
   * 让用户可以立即在同一绘制会话中重新点击开始，无需退出再进入。
   */
  private handleDrawFail(reason: string) {
    this.points = [];
    this.lastClickInfo = null;
    this.renderer.clear();
    this.host.options?.onDrawError?.(reason);
  }

  private finish() {
    if (!this.drawing) return;

    const validation = this.validator.validatePolygon(this.points);
    if (!validation.valid) {
      const text = DrawingValidator.getReasonText(validation.reason!);
      this.handleDrawFail(text);
      return;
    }

    const fId = `popOverlay${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const closedCoordinates = [...this.points, this.points[0]];

    try {
      this.host.drawPolygon(fId, closedCoordinates, AnchorLayerType.MOVE_ROUND, {});
    } catch (err) {
      console.error('[DrawingInteraction] drawPolygon failed', err);
      this.handleDrawFail('绘制多边形失败，请重试');
      return;
    }

    const feature = this.layers.polygons[this.layers.polygons.length - 1];

    this.drawing = false;
    this.points = [];
    this.lastClickInfo = null;
    this.renderer.clear();
    this.unbindEvents();

    this.host.options?.drawCallBack?.(
      true,
      feature,
      fId,
      closedCoordinates
    );
  }
}
